import { getScheduleList } from 'src/services/api/schedule';
import { useCallback, useState } from 'react';
import { ICalendar, IInterval } from '../../../../entities/calendar';
import { IScheduleLists } from '../../../../entities/schedule';
import { ISchedule } from 'src/entities/schedule';
import { IServiceDetails } from 'src/entities/service';
import { EventInput, EventApi, EventSourceFunc, ViewApi } from '@fullcalendar/core';
import hu from '@fullcalendar/core/locales/hu';
import de from '@fullcalendar/core/locales/de';
import sk from '@fullcalendar/core/locales/sk';
import enGb from '@fullcalendar/core/locales/en-gb';
import getDay from 'date-fns/getDay';
import { getServicesByCalendar } from '../../../../services/api/service';
import { useSubscription } from 'src/hooks';
import { IntlShape } from 'react-intl';
import {
    isFullSchedule,
    EventMobile,
    Event,
    isPassedSchedule,
    isWeekend,
} from 'src/components/BookingFlow/modules/TimeTableSelect/Event';
import MediaQuery from 'react-responsive';
import React from 'react';
import subDays from 'date-fns/fp/subDays';

const locales = { hu, de, sk };

export interface IEventRenderInfo {
    isMirror: boolean;
    isStart: boolean;
    isEnd: boolean;
    event: EventApi & {
        extendedProps: { schedule: ISchedule; service: IServiceDetails };
    };
    el: HTMLElement;
    view: ViewApi;
}

export type IEventTimes = Record<string, IInterval[]>;

export const getFullCalendarLocale = (locale: string) => {
    const fallbackCalendarLocale = enGb;
    if (locale === 'en') {
        return enGb;
    }

    const calendarLocale = locales[locale as keyof typeof locales];
    return calendarLocale ?? fallbackCalendarLocale;
};

export const mapScheduleResponse = (
    schedules: IScheduleLists,
    services: IServiceDetails[]
): EventInput[] =>
    Object.values(schedules)
        .flat()
        .map((schedule) => ({
            schedule,
            service: services.find((s) => s.Id === schedule.ServiceId),
        }))
        .map(({ schedule, service }) => ({
            title: service!.Name,
            start: schedule.Start,
            end: schedule.End,
            borderColor: undefined,
            extendedProps: {
                schedule,
                service,
            },
        }));

export const useSchedules = (calendar: ICalendar) => {
    const getServices = useCallback(() => getServicesByCalendar(calendar.Id), [calendar.Id]);
    const [services, areServicesLoading] = useSubscription(getServices, []);

    const [eventSourceParams, setEventSourceParams] = useState<
        Parameters<EventSourceFunc> | undefined
    >(undefined);

    const getSchedules: EventSourceFunc = useCallback(
        (...arg: Parameters<EventSourceFunc>) => setEventSourceParams(arg),
        []
    );

    const fetchSchedules = useCallback(async () => {
        if (eventSourceParams && !areServicesLoading) {
            const [{ start, end: exclusiveEnd }, successCallback, failureCallback] =
                eventSourceParams;

            const end = subDays(1, new Date(exclusiveEnd));

            try {
                const result = await getScheduleList(
                    calendar.Id,
                    start.toISOString(),
                    end.toISOString(),
                    true
                );
                successCallback(mapScheduleResponse(result, services));
                return result;
            } catch (e) {
                failureCallback(e);
                throw e;
            }
        }

        return null;
    }, [
        services,
        areServicesLoading,
        eventSourceParams && eventSourceParams[0].start.toISOString(),
        eventSourceParams && eventSourceParams[0].end.toISOString(),
        eventSourceParams && eventSourceParams[1],
        eventSourceParams && eventSourceParams[2],
    ]);

    const [scheduleLists, isLoading, error] = useSubscription(fetchSchedules);

    return {
        getSchedules,
        scheduleLists,
        isLoading: areServicesLoading || isLoading,
        error,
    };
};

export const handleEventClassNames = (
    arg: IEventRenderInfo & {
        minTimeReserve: number;
        firstFreeSlot?: string;
    }
) => {
    const { event, minTimeReserve, firstFreeSlot } = arg;
    const { schedule } = event.extendedProps;

    if (typeof schedule === 'undefined') {
        return '';
    }

    return isFullSchedule(schedule.FreeSeatsCount) ||
    isPassedSchedule(schedule.Start, minTimeReserve, firstFreeSlot)
        ? 'fc-event-disabled'
        : '';
};

export const handleEventRender: (
    arg: IEventRenderInfo & {
        handleServiceSelect: (
            service: IServiceDetails,
            schedule: ISchedule,
            personCount: number
        ) => void;
        intl: IntlShape;
        currentWeekDay: number | false;
        minTimeReserve: number;
        isReadonly: boolean;
        hideSeatCount: boolean;
        isWeekendDisabled: boolean;
        isPaymentEnabled?: boolean;
        firstFreeSlot?: string;
    }
) => JSX.Element | null = ({
    event,
    handleServiceSelect,
    currentWeekDay,
    minTimeReserve,
    view,
    isReadonly,
    hideSeatCount,
    isWeekendDisabled,
    isPaymentEnabled,
    firstFreeSlot
}) => {
    const { schedule, service } = event.extendedProps as {
        service: IServiceDetails;
        schedule: ISchedule;
    };

    const day = getDay(new Date(schedule.Start));

    if (currentWeekDay !== false && day !== currentWeekDay) {
        return null;
    }

    if (isWeekendDisabled && isWeekend(day)) {
        return null;
    }

    const eventProps = {
        schedule,
        service,
        handleServiceSelect,
        minTimeReserve,
        isReadonly,
        hideSeatCount,
        isPaymentEnabled,
        firstFreeSlot
    };

    return (
        <MediaQuery query="(max-width: 48rem)">
            {(matches) =>
                matches ? (
                    <EventMobile {...eventProps} isListView={view.type.includes('list')} />
                ) : (
                    <Event {...eventProps} />
                )
            }
        </MediaQuery>
    );
};